// /pay: MyFatoorah payment page. Authenticated only by the session token + nonce in the URL;
// every call is re-validated on the server and amounts come from the server document.
import { $, $$, api, esc, isAr, money, t } from "./util";

let busy = false;

function show(sel, on = true) { const el = $(sel); if (el) el.hidden = !on; }
function fail(msg) {
	const el = $("[data-pay-error]");
	el.textContent = msg; el.hidden = false; show("[data-pay-loading]", false);
	el.scrollIntoView({ behavior: "smooth", block: "center" });
}

async function execute(params) {
	if (busy) return;
	busy = true;
	show("[data-pay-error]", false);
	try {
		const r = await api("darat.payments.myfatoorah.pay_execute", params, "POST");
		if (!r || !r.payment_url || !/^https:\/\//i.test(r.payment_url)) throw new Error(t("تعذّر بدء الدفع", "Could not start the payment"));
		location.href = r.payment_url;  // 3-D Secure / OTP at the bank
	} catch (e) {
		busy = false;
		fail(e.message || t("تعذّر بدء الدفع، حاول مرة أخرى.", "Could not start the payment, please try again."));
	}
}

// MyFatoorah's hosted Apple Pay method, in every browser: Safari shows the Apple Pay sheet, other browsers
// (Chrome, Edge, Firefox on Windows / Mac / Android) show Apple's QR code to finish on the customer's iPhone
function appleHosted(ctx) {
	return (ctx.methods || []).find((m) => String(m.code).toLowerCase() === "ap") || null;
}
const APPLE_LOGO = `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>`;

function loadScript(src) {
	return new Promise((res, rej) => { const s = document.createElement("script"); s.src = src; s.async = true; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
}

// MyFatoorah builds its elements inside #mf-embed: label its divider in our words and mark the parts so
// store.css can lay them out (wallets first, then card fields and the pay button). Nothing is moved:
// moving an iframe would reload it.
function tidyEmbed() {
	const box = document.getElementById("mf-embed");
	if (!box) return;
	const apply = () => {
		[...box.children].forEach((el) => {
			if (el.tagName === "DIV" && !el.dataset.part && el.querySelector("div")) {
				el.dataset.part = "divider";
				const mid = el.children[1];
				if (mid) mid.textContent = box.parentElement.classList.contains("has-wallets") ? t("أو ادفع بالبطاقة", "or pay by card") : t("بيانات البطاقة", "Card details");
			}
		});
	};
	apply();
	new MutationObserver(apply).observe(box, { childList: true });
}

export async function initPay() {
	const page = $("[data-pay-page]");
	if (!page) return;
	const qs = new URLSearchParams(location.search);
	const token = qs.get("token"), k = qs.get("k");
	if (!token || !k) return fail(t("رابط الدفع غير صالح.", "This payment link is not valid."));
	let ctx;
	try {
		ctx = await api("darat.payments.myfatoorah.pay_context", { token, k }, "POST");
	} catch (e) {
		return fail(e.message || t("انتهت صلاحية رابط الدفع. ارجع إلى صفحة طلبك واضغط «ادفع الآن».", "This payment link has expired. Go back to your order page and press “Pay now”."));
	}
	show("[data-pay-loading]", false);
	show("[data-pay-test]", !!ctx.sandbox);
	$("[data-pay-amount]").textContent = money(ctx.amount);
	$("[data-pay-desc]").textContent = ctx.description || "";
	if (ctx.expires_in) {
		const end = Date.now() + ctx.expires_in * 1000;
		const tick = () => {
			const m = Math.max(0, Math.round((end - Date.now()) / 60000));
			$("[data-pay-expiry]").textContent = t(`صالح لمدة ${m} دقيقة`, `Valid for ${m} min`);
		};
		tick(); setInterval(tick, 30000);
	}

	let embeddedOk = false;
	if (ctx.embedded) {
		try {
			await loadScript(ctx.embedded.js);
			show("[data-pay-embed-wrap]");
			// Apple Pay comes from the server only when the domain is registered (apple_pay_ready); Apple shows it in
			// Safari, and in other browsers as a QR code for the iPhone.
			// Google Pay only on Android: elsewhere its button often can't render and MyFatoorah leaves an empty slot.
			const options = ctx.embedded.options.filter((o) => o !== "GooglePay" || /Android/i.test(navigator.userAgent));
			const hostedApple = !options.includes("ApplePay") && appleHosted(ctx);
			if (hostedApple) {
				// Apple Pay before our domain is registered: the official button here, finished on MyFatoorah's page
				$("[data-pay-express-label]").insertAdjacentHTML("afterend", `<button type="button" class="ds-applepay" data-apple-hosted lang="${isAr ? "ar" : "en"}" aria-label="Apple Pay">${APPLE_LOGO}<span>Pay</span></button>`);
				$("[data-apple-hosted]").addEventListener("click", (e) => {
					const b = e.currentTarget; b.disabled = true;
					execute({ token, k, payment_method_id: hostedApple.id }).finally(() => { b.disabled = false; });
				});
			}
			const embedWallets = options.filter((o) => o !== "Card");
			const wrap = $("[data-pay-embed-wrap]");
			wrap.classList.toggle("has-wallets", !!(embedWallets.length || hostedApple));  // divider says "or pay by card"
			wrap.classList.toggle("wallets-2", embedWallets.length >= 2);                // embedded wallets side by side
			wrap.classList.toggle("has-card", options.includes("Card"));
			show("[data-pay-express-label]", !!(embedWallets.length || hostedApple));
			const wallet = { style: { frameHeight: "52px", button: { height: "52px", borderRadius: "12px", type: "plain" } } };
			window.myfatoorah.init({
				sessionId: ctx.embedded.sessionId, countryCode: ctx.embedded.countryCode, currencyCode: ctx.currency,
				amount: ctx.amount, containerId: "mf-embed", language: isAr ? "ar" : "en", paymentOptions: options,
				callback: (r) => {
					if (r && r.isSuccess && r.sessionId) return execute({ token, k, session_id: r.sessionId });
					// say which method failed: a wallet error is not a card-details problem
					const type = String((r && r.paymentType) || "").toLowerCase();
					const wallet = { applepay: "Apple Pay", googlepay: "Google Pay", stcpay: "STC Pay" }[type];
					fail(wallet
						? t(`لم يكتمل الدفع عبر ${wallet} ولم يُخصم أي مبلغ. حاول مرة أخرى أو ادفع بالبطاقة.`, `${wallet} didn't complete and nothing was charged. Try again or pay by card.`)
						: t("تحقق من بيانات البطاقة وحاول مرة أخرى.", "Check the card details and try again."));
				},
				settings: {
					card: { style: { hideNetworkIcons: false, cardHeight: 192,
						input: { inputHeight: "46px", inputMargin: "6px", borderRadius: "10px", outerRadius: "12px", fontSize: "15px", color: "#1A2630", fontFamily: "sans-serif" },
						button: { useCustomButton: false, textContent: `${t("ادفع", "Pay")} ${money(ctx.amount)}`, fontSize: "16px",
							fontFamily: "inherit", color: "#ffffff", backgroundColor: "#0A7FAE", height: "54px", borderRadius: "14px", width: "100%" } } },
					applePay: wallet, googlePay: wallet, stcPay: wallet,
				},
			});
			tidyEmbed();
			embeddedOk = true;
		} catch (e) {
			show("[data-pay-embed-wrap]", false);
		}
	}
	const methods = (ctx.methods || []).filter((m) => !embeddedOk || !m.embedded);
	if (methods.length) {
		show("[data-pay-methods-wrap]");
		if (!embeddedOk) $("[data-pay-methods-title]").textContent = t("اختر وسيلة الدفع", "Choose how to pay");
		$("[data-pay-methods]").innerHTML = methods.map((m) => `<button type="button" class="ds-paymethod" data-method="${esc(m.id)}">
			${m.image ? `<img src="${esc(m.image)}" alt="" loading="lazy">` : ""}<span>${esc(isAr ? (m.ar || m.en) : (m.en || m.ar))}</span></button>`).join("");
		$$("[data-method]").forEach((b) => b.addEventListener("click", () => {
			$$("[data-method]").forEach((x) => (x.disabled = true));
			b.classList.add("is-busy");
			execute({ token, k, payment_method_id: b.dataset.method });
		}));
	}
	if (!embeddedOk && !methods.length) fail(t("لا توجد وسائل دفع متاحة حالياً.", "No payment methods are available right now."));
}

// order page «ادفع الآن»: a fresh payment session for this unpaid order
document.addEventListener("click", async (e) => {
	const b = e.target.closest("[data-pay-order]");
	if (!b || b.disabled) return;
	b.disabled = true;
	const err = $("[data-pay-order-err]");
	if (err) err.hidden = true;
	try {
		const r = await api("darat.payments.store.pay_order", { token: b.dataset.payOrder }, "POST");
		if (r && r.url) location.href = r.url;
	} catch (ex) {
		b.disabled = false;
		if (err) { err.textContent = ex.message; err.hidden = false; }
	}
});
